/** @jsx jsx */
import { jsx, css } from '@emotion/core'
import R from 'ramda'
import React, {
  useState,
  useMemo,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from 'react'
import { List } from 'immutable'
import Task from 'data.task/lib/task'
import { strCol } from '@integec/grid-tools/lib/cols'
import { ExportComponent } from 'core'
import { connect } from 'react-redux'
import { Button, Input, Empty, message } from 'antd'
import { utilityHeader, unitIdHeader } from 'core/HeaderColTypeProps'
import {
  getApiTargetInfo,
  getUtilityNameMap,
  getUnitNameMap,
  getPermitActions,
} from 'core/selectors'
import * as api from '../api/pgads'
import {
  Content,
  CriteriaRow,
  Filler,
  FullHeightSizer,
  Title,
} from 'core/screen-util'
import Grid, {
  defaultVirtualizedCellRender,
  virtualizedGridRenderer,
} from '@integec/grid-tools'
import { rowHeaderRender, exportDataComponent } from 'core/utils'
import { ErrorContext, SpinContext } from 'context'
import * as ScreenPermissions from 'core/EventActions'
import UnitPerfEditModal from './UnitPerfEditModal'

const title = 'Unit Performance Group'
const totalWidth = 1030
const groupTypes = ['Region', 'Fuel']

const UnitPerfGroup = ({
  utilityNameMap,
  unitNameMap,
  actionPermits,
  apiTargetInfo,
}) => {
  const [data, setData] = useState([])
  const [modalInfo, setModalInfo] = useState()
  const closePopup = () => setModalInfo()
  const [fuzzyFilter, setFuzzyFilter] = useState('')
  const [selectedRows, setSelected] = useState([])
  const [groups, setGroups] = useState([])
  const [allUnits, setUnits] = useState([])
  const { setError } = useContext(ErrorContext)
  const { setSpinning } = useContext(SpinContext)

  const writePermission = actionPermits.includes(
    ScreenPermissions.CHANGE_PERMISSIONS
  )

  const GridRef = useRef()

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    List([api.getAllUnitInfo(apiTargetInfo), api.getGroups(apiTargetInfo)])
      .traverse(Task.of, _ => _)
      .fork(setError, data => {
        setUnits(
          R.sortBy(R.compose(R.toLower, R.prop('unitName')))(data.get(0))
        )
        setGroups(data.get(1))
      })
  }, [apiTargetInfo, setError])

  const loadData = useCallback(() => {
    setSpinning(true)
    api.getUnitPerfGroup(apiTargetInfo).fork(setError, data => {
      setData(data)
      setSpinning(false)
    })
  }, [apiTargetInfo, setError, setSpinning])

  const headerWithFormatter = useMemo(
    _ =>
      UnitPerfGroupHeaders.map(h =>
        h.ident === 'utility'
          ? {
              ...h,
              dataFormatter: ({ value }) =>
                value + ' - ' + utilityNameMap[value] || value,
              sortIndexGetter: ({ rowData }) =>
                utilityNameMap[rowData.utility] || rowData.utility,
              alignment: 'left',
            }
          : h.ident === 'unitId'
          ? {
              ...h,
              choices: [],
              dataFormatter: ({ value, rowData }) =>
                rowData.unit + ' - ' + unitNameMap[value] || value,
              sortIndexGetter: ({ rowData }) =>
                unitNameMap[rowData.unit] || rowData.unit,
              alignment: 'left',
            }
          : h
      ),
    [unitNameMap, utilityNameMap]
  )

  const popModal = ({ isNew }) => {
    if (isNew) {
      setModalInfo({ isNew })
    } else {
      if (selectedRows.length === 0) message.warn('select single row to update')

      const editInfo = selectedRows[0]
      setModalInfo({
        ...editInfo,
        units: R.groupBy(e => e.groupName)(data)[editInfo.groupName].map(
          u => u.unitId
        ),
      })
    }
  }

  const cellRenderer = props =>
    defaultVirtualizedCellRender({
      ...props,
      onDoubleClick: () => popModal({}),
    })

  const validateData = ({ groupType, groupName, units }) => {
    if (!groupType || !groupName) {
      message.error(
        'Please, select ' +
          (!groupType ? 'Group Type' : 'Group Name') +
          ' to save'
      )
      return false
    } else if (units.length === 0) {
      message.error('Please, select Unit to save')
      return false
    }

    return true
  }

  const handleSave = ({ isNew, groupData }) => {
    const { groupName, groupType, units = [] } = groupData

    if (!isNew && units.length === 0) {
      // deleting whole group
      api
        .deleteUnitPerfGroup(apiTargetInfo)({ group: groupName })
        .fork(setError, _ => {
          closePopup()
          loadData()
        })
      return
    }

    if (!validateData(groupData)) return
    api
      .saveUnitPerfGroup(apiTargetInfo)({
        isNew,
        data: units.map(u => ({
          groupType,
          groupName,
          utility: u.substring(0, 3),
          unit: u.substring(3),
        })),
      })
      .fork(setError, _ => {
        closePopup()
        loadData()
      })
  }

  return (
    <div>
      {modalInfo && (
        <UnitPerfEditModal
          tableData={data}
          data={modalInfo}
          allUnits={allUnits}
          groupTypes={groupTypes}
          groups={groups.filter(
            g => !R.uniq(data.map(e => e.groupName)).includes(g.groupName)
          )}
          onOK={handleSave}
          closePopup={closePopup}
          visible
        />
      )}
      <CriteriaRow>
        <Title
          style={{
            marginRight: '15px',
            marginBottom: '5px',
            // ...titleProps,
          }}
        >
          {title}
        </Title>
        <Button
          onClick={() => loadData()}
          style={{ marginLeft: '10px' }}
          type="primary"
          icon="database"
        >
          Retrieve
        </Button>

        {writePermission && (
          <>
            <Button
              onClick={() => popModal({ isNew: true })}
              icon="plus-circle"
              style={{ backgroundColor: '#e6f7ff', marginLeft: '10px' }}
            >
              Insert
            </Button>
            <Button
              onClick={() => popModal({})}
              icon="edit"
              type="primary"
              disabled={selectedRows.length <= 0}
              style={{ marginLeft: '10px' }}
            >
              Update
            </Button>
          </>
        )}

        <Filler />

        <Input.Search
          placeholder="Filter"
          value={fuzzyFilter}
          css={{
            marginLeft: '10px',
            marginRight: '10px',
            flex: '0 0 200px',
            // ...(searchStyle || {}),
          }}
          onChange={e => setFuzzyFilter(e.target.value)}
        />
        <ExportComponent
          getData={_ => exportDataComponent(GridRef, data, fuzzyFilter)}
          data={data}
          headers={(headerWithFormatter || []).map(d => ({
            ...d,
            rowHeaderBackgroundColor: '#eee',
          }))}
          fileName={title}
          btnStyle={{ marginRight: '10px' }}
          disabled={data.length <= 0}
        />
      </CriteriaRow>

      <FullHeightSizer heightOffset={220}>
        {({ height, _ }) =>
          data && data.length > 0 ? (
            <Content style={{ height: height - 20 }}>
              <Grid
                ref={GridRef}
                editMode="cell"
                data={data || []}
                headers={headerWithFormatter.map(e => ({
                  ...e,
                  isFiltered: true,
                }))}
                altBy={d => d.unitId}
                altBgColor="#ebebeb"
                render={virtualizedGridRenderer({
                  autoFixColByKey: false,
                  rowHeaderRender,
                  cellRender: cellRenderer,
                  height: height - 40,
                  width: totalWidth,
                  headerRowHeight: 60,
                })}
                fuzzyFilter={fuzzyFilter}
                onSelectionChange={({ selectedRows }) =>
                  setSelected(selectedRows)
                }
                initialSortOptions={headerWithFormatter}
                sortOptions={headerWithFormatter}
                sortEnabled
                selectionType="row"
                selectionMode="single"
              />
            </Content>
          ) : (
            <Content style={{ height: '200px' }}>
              <Empty
                imageStyle={{ paddingTop: '30px' }}
                description="Data Not Available"
              />
            </Content>
          )
        }
      </FullHeightSizer>
    </div>
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  actionPermits: getPermitActions(state),
  utilityNameMap: getUtilityNameMap(state),
  unitNameMap: getUnitNameMap(state),
})

export default connect(mapStateToProps)(UnitPerfGroup)

export const UnitPerfGroupHeaders = [
  strCol({
    ident: 'groupType',
    display: 'Group Type',
    isKey: true,
    width: 150,
    alignment: 'left',
    sortOrder: 'asc',
  }),
  strCol({
    ident: 'groupName',
    display: 'Group Name',
    isKey: true,
    width: 180,
    alignment: 'left',
    sortOrder: 'asc',
  }),
  utilityHeader,
  unitIdHeader,
]
